import { z } from 'zod'

declare const storageKeyBrand: unique symbol

/**
 * Opaque object key written by private R2 uploads (`images/<uuid>.jpg`).
 * Distinct from browser-usable URLs so the type system can catch mix-ups.
 */
export type StorageKey = string & { readonly [storageKeyBrand]: true }

/**
 * Private object-storage key shape. No scheme, no `..`, at least one `/`.
 * The predicate is what brands the string: the checks are the proof.
 */
export function isStorageKey(value: string): value is StorageKey {
  if (!value || value.includes('://') || value.startsWith('data:')) return false
  if (value.includes('..') || value.startsWith('/') || value.includes('\\')) return false
  return /^[a-zA-Z0-9][a-zA-Z0-9._/-]*\/[a-zA-Z0-9._/-]+$/.test(value)
}

/** Brand a string that has already been validated as a storage key. */
export function asStorageKey(value: string): StorageKey {
  if (!isStorageKey(value)) {
    throw new Error(`Not a storage object key: ${value}`)
  }
  return value
}

/** Brand when valid; otherwise null. */
export function toStorageKey(value: string): StorageKey | null {
  return isStorageKey(value) ? value : null
}

/**
 * Zod schema for a private storage key (output is branded).
 *
 * The return type names `StorageKey`. Inferring the pipe instead inlines the
 * private brand symbol, which declaration emit cannot name from another file.
 */
function storageKeyFromString(): z.ZodType<StorageKey, string> {
  return z
    .string()
    .min(1)
    .max(1_000)
    .refine((value) => isStorageKey(value), 'Must be a storage object key')
    .transform((value): StorageKey => {
      // refine does not narrow, so the predicate runs again and is what brands it.
      if (!isStorageKey(value)) throw new Error(`Not a storage object key: ${value}`)
      return value
    })
}

/** Zod schema for a private storage key (output is branded). */
export const storageKeySchema = storageKeyFromString()

/** Inline image data URL (PNG/JPEG/WebP/GIF), base64. */
export const dataImageUrlSchema = z
  .string()
  .max(700_000, 'Image is too large — use a smaller thumbnail')
  .regex(
    /^data:image\/(png|jpeg|jpg|webp|gif);base64,/,
    'Must be a base64 image data URL (png/jpeg/webp/gif)',
  )

/** Absolute http(s) URL. */
export const httpUrlSchema = z.string().url().max(4_000).refine((v) => /^https?:\/\//.test(v), 'Must be an http(s) URL')

/**
 * Browser-usable asset on the wire after the API signs private objects:
 * https URL (signed or external) or inline data URL when storage is off.
 */
export const browserAssetUrlSchema = z
  .string()
  .min(1)
  .max(700_000)
  .refine(
    (v) => /^https?:\/\//.test(v) || /^data:image\/(png|jpeg|jpg|webp|gif);base64,/.test(v),
    'Must be an http(s) or image data URL',
  )

/**
 * A value the client may send for an image write field:
 * - private storage key (preferred after upload)
 * - external http(s) URL
 * - inline data URL (fallback when object storage is off)
 */
export const imageAssetRefSchema = z
  .string()
  .max(700_000, 'Image is too large — use a smaller thumbnail')
  .refine(
    (v) =>
      isStorageKey(v) ||
      /^https?:\/\//.test(v) ||
      /^data:image\/(png|jpeg|jpg|webp|gif);base64,/.test(v),
    'Must be a storage key, http(s) URL, or image data URL',
  )

/**
 * Avatar write ref (same shapes as image assets; smaller max for non-thumbnail
 * misuse of huge payloads).
 */
export const avatarAssetRefSchema = z
  .string()
  .max(4_000)
  .refine(
    (v) =>
      isStorageKey(v) ||
      /^https?:\/\//.test(v) ||
      /^data:image\/(png|jpeg|jpg|webp|gif);base64,/.test(v),
    'Avatar must be a storage key, http(s) URL, or image data URL',
  )

declare const httpUrlBrand: unique symbol

/** An absolute http(s) URL. Distinct from a storage key and from a data URL. */
export type HttpUrl = string & { readonly [httpUrlBrand]: true }

declare const dataImageUrlBrand: unique symbol

/** An inline image data URL. Distinct from a storage key and from an http URL. */
export type DataImageUrl = string & { readonly [dataImageUrlBrand]: true }

/** Brand when the string is an absolute http(s) URL. */
export function isHttpUrl(value: string): value is HttpUrl {
  return httpUrlSchema.safeParse(value).success
}

/** Brand when the string is an inline image data URL. */
export function isDataImageUrl(value: string): value is DataImageUrl {
  return dataImageUrlSchema.safeParse(value).success
}

/**
 * What we persist in avatarUrl / imageUrl / document url columns after
 * normalize: key, external URL, or data URL — never a presigned GET.
 *
 * Not `imageAssetRefSchema`'s output: that schema infers a plain string, so a
 * union with it collapses to `string` and the storage-key brand disappears.
 */
export type StoredAssetRef = StorageKey | HttpUrl | DataImageUrl

/** The persisted form of a write ref, or null when it is none of the three. */
export function toStoredAssetRef(value: string): StoredAssetRef | null {
  if (isStorageKey(value)) return value
  if (isHttpUrl(value)) return value
  if (isDataImageUrl(value)) return value
  return null
}
